/**
 * SampleAnalysis - Public, no-login "see a real analysis before you sign up" page.
 *
 * Plays a REAL analyzed clip (a triple Salchow) with the pose skeleton burned in
 * by the production pipeline, alongside the REAL coaching feedback and metrics
 * pulled from the video_history table. Nothing here is mocked — it's actual
 * LumiSkating output. Ends with a free-during-beta call to action.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SEO from '../components/SEO';
import { trackEvent } from '../utils/analytics';
import './SampleAnalysis.css';

const VIDEO_URL = '/sample/triple-salchow.mp4';

// Real metrics for this clip (video_history.biomechanical_results)
const METRICS = [
  { icon: 'fa-arrow-up', label: 'Jump Height', value: '50.3', unit: 'cm' },
  { icon: 'fa-arrows-rotate', label: 'Rotations', value: '3.35', unit: 'rev' },
  { icon: 'fa-gauge-high', label: 'Peak Rotation', value: '5.52', unit: 'rev/s' },
  { icon: 'fa-stopwatch', label: 'Air Time', value: '0.63', unit: 's' },
];

// Real coaching feedback for this clip (video_history.coaching_feedback)
const SAMPLE_COACHING = `## Overall Assessment
This is a powerhouse **Triple Salchow** characterized by exceptional height (**50.3 cm**) and quad-level peak rotation speeds (**5.52 rev/s**). While you complete **3 rotations**, the **GOE** is penalized by a sluggish arm snap and a near-complete loss of momentum on the landing. You have the raw power for more difficult content, but we must refine the efficiency to protect your back and ensure "Strategic Consistency."

## Technical Strengths
*   **Vertical Explosiveness:** Your jump height of **50.3 cm** and air time of **0.63s** are well above elite senior standards, providing a massive window for rotation.
*   **Rotation Mechanics:** Once in position, your **Leg Wrap (99%)** and **Arm Closure (17%)** are textbook. Your peak rotation speed of **5.52 rev/s** is actually within the elite range for quadruple jumps.
*   **Landing Stability:** Despite a low exit speed, you maintained a **1.00 stability rating**, showing excellent control of the core upon impact.

## Priority Improvements
*   **Snap Quickness:** Your arm closure takes **0.533s** (16 frames), significantly slower than the elite target of **0.07-0.13s**. This delay forces you to work harder in the air than necessary.
*   **Landing Flow:** Your landing speed drops to **0.7 km/h**. We need to convert that vertical power into a longitudinal glide to maximize GOE.

## Recommended Drills
*   **Snap-to-Tight Back Spin**: Focus exclusively on the speed of the arm "snap." From a wide entry, pull into a scratch spin as fast as possible. Target: instantaneous closure.
*   **Landing Power Pulls**: Immediately upon landing a 2S or 3S, go directly into three powerful back-inside power pulls. This trains you to keep the blade moving after impact.

## Next Session Focus
*   **Primary Goal**: Reduce arm closure time from **0.53s** to under **0.25s** for a cleaner, higher-GOE jump.`;

function SampleAnalysis() {
  const navigate = useNavigate();

  // Match the landing page's gold theme; restore on leave.
  useEffect(() => {
    const prev = document.body.getAttribute('data-theme');
    document.body.setAttribute('data-theme', 'gold');
    return () => {
      document.body.setAttribute('data-theme', localStorage.getItem('theme') || prev || 'gold');
    };
  }, []);

  useEffect(() => {
    trackEvent('sample_view');
  }, []);

  const goSignup = () => {
    trackEvent('cta_click', { location: 'sample' });
    navigate('/?signup=1');
  };

  return (
    <div className="sample-page">
      <SEO
        title="See a Real Analysis — LumiSkating"
        description="Watch LumiSkating's AI break down a real triple Salchow: 33-point pose tracking, jump height, rotation speed, and coaching feedback. Try it free during beta."
        url="https://www.lumiskating.com/sample"
      />

      {/* Header */}
      <header className="sample-header">
        <button className="sample-brand" onClick={() => navigate('/')}>
          <img src="/skate.png" alt="LumiSkating" width="34" height="34" />
          <span>Lumi<span className="sample-brand-skating">Skating</span></span>
        </button>
        <div className="sample-header-cta">
          <button className="sample-link" onClick={() => navigate('/?signin=1')}>Sign In</button>
          <button className="sample-btn-gold" onClick={goSignup}>Start Free</button>
        </div>
      </header>

      {/* Intro */}
      <section className="sample-intro">
        <div className="sample-badge"><span className="sample-pulse" /> Real analysis · Triple Salchow</div>
        <h1>This is what LumiSkating sees</h1>
        <p>
          A real jump, broken down by the same AI you'll use — 33-point pose tracking, biomechanics,
          and coaching feedback. No sign-up required to look around.
        </p>
      </section>

      {/* Player (pose skeleton burned in by the pipeline) */}
      <section className="sample-stage">
        <div className="sample-player">
          <div className="sample-video-wrap">
            <video
              src={VIDEO_URL}
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="auto"
            />
            <div className="sample-live-tag">
              <span className="sample-live-dot" /> AI pose tracking
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="sample-metrics">
          {METRICS.map((m) => (
            <div className="sample-metric-card" key={m.label}>
              <div className="sample-metric-icon"><i className={`fas ${m.icon}`} /></div>
              <div className="sample-metric-value">
                {m.value}{m.unit && <span className="sample-metric-unit"> {m.unit}</span>}
              </div>
              <div className="sample-metric-label">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Coaching */}
      <section className="sample-coaching">
        <div className="sample-coaching-head">
          <img src="/skate.png" alt="" width="28" height="28" />
          <span>Lumi&rsquo;s coaching feedback</span>
        </div>
        <div className="sample-coaching-body">
          <ReactMarkdown>{SAMPLE_COACHING}</ReactMarkdown>
        </div>
      </section>

      {/* CTA */}
      <section className="sample-cta">
        <h2>Now analyze your own jumps &amp; spins</h2>
        <p>Free during beta · No credit card · Unlimited AI analysis</p>
        <button className="sample-btn-gold large" onClick={goSignup}>
          Start Free <i className="fas fa-long-arrow-alt-right" />
        </button>
        <p className="sample-cta-note">Takes ~2 minutes per clip. Upload from any phone or camera.</p>
      </section>
    </div>
  );
}

export default SampleAnalysis;
